import { I18nThemed } from 'interfaces/mixins';
import { DNode } from '@dojo/framework/core/interfaces';
import { v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { Radio } from '@dojo/widgets/radio';

import { FormLikertField, FormData } from '../interfaces';
import css from '../css/form.m.css';

interface LikertHeadProperties {
	heads: string[];
}

class LikertHead extends I18nThemed<LikertHeadProperties> {
	protected render(): DNode {
		const { heads } = this.properties;
		const kids = heads.map((hd, key) => v('th', { key }, [hd]));
		return v('thead', {}, [v('th', { key: 'blank' }), ...kids]);
	}
}

interface LikertRowProperties {
	name: string;
	question: string;
	answers: string[];
	index: number;
	formdata: FormData;
}

class LikertRow extends I18nThemed<LikertRowProperties> {
	protected render(): DNode {
		const { name: nm, question, answers, index, formdata } = this.properties;
		const name = `${nm}.${index}`;
		const kids = answers.map((ans, idx) => {
			const value = `${index + 1}: ${ans}`;
			const key = `ans-${index}-${idx}`;
			const checked = formdata[nm][index] === value;
			return v('td', { key }, [
				w(Radio, {
					key: `radio-${index}-${idx}`,
					name,
					value,
					aria: {
						label: value,
						checked: `${checked}`,
						role: 'radio'
					},
					checked: checked,
					onValue: (checked: boolean) => {
						if (checked) {
							formdata[nm][index] = value;
						}
						this.invalidate();
					}
				})
			]);
		});
		const cls = index % 2 === 1 ? 'odd' : 'even';
		return v('tr', { key: `row-${index}`, classes: [cls] }, [
			v('td', { key: `question-${index}` }, [question]),
			...kids
		]);
	}
}

interface LikertBodyProperties {
	name: string;
	questions: string[];
	answers: string[];
	formdata: FormData;
}

class LikertBody extends I18nThemed<LikertBodyProperties> {
	protected render(): DNode {
		const { name, questions, answers, formdata } = this.properties;
		if (formdata[name] === undefined || formdata[name] === null) {
			formdata[name] = new Array(questions.length).fill('');
		}
		const kids = questions.map((question, index) => w(LikertRow, { question, index, answers, name, formdata }));
		return v('tbody', {}, kids);
	}
}

export interface FormLikertProperties extends FormLikertField {}

@theme(css)
export class FormLikert extends I18nThemed<FormLikertProperties> {
	protected render(): DNode {
		const { id: name, likertQuestions, likertAnswers, title, formdata } = this.properties;

		return v('fieldset', { classes: [this.theme(css.formLikert)] }, [
			v('legend', {}, [title]),
			v('table', { classes: ['likert', 'listing'] }, [
				w(LikertHead, { heads: likertAnswers }),
				w(LikertBody, { name, questions: likertQuestions, answers: likertAnswers, formdata })
			])
		]);
	}
}
